import "./style.scss";
import { initMealz } from "./init-mealz";
import {
  getPosExtId,
  isAvailable,
  waitForVariable,
  buildCoursesUSSRHeaders,
  clearPreferences,
} from "./utils";
import { injectCatalog, listenHeaderHeightChange } from "./catalog";
import { injectPlanner } from "./planner";
import { initRecapPage, initRecipeRecapButton, prefetchBasketPreview } from "./recipe-recap";
import { initShelvesCardInjection, isShelf, prefetchRecipeCards } from "./recipe-cards";
import { initBasketTagInjection, prefetchRecipeTags } from "./recipe-tags";
import { injectOrderButtons, isOrderButtonsPage, prefetchOrderButtons } from "./order-buttons";
import { injectRecipeCardsById } from "./recipe-card-by-id";
import { injectCore } from "./mealz-core";
import { callSsrHandlePayment, isOnConfirmationPage, hasPayment } from "./basket-sync";
import { fetchSsrStyles } from "../../core/common/ssr-utils";
import { getSsrBaseUrl } from "./coursesu-env.js";


/*********************************** FEATURES DETECTION ***********************************/

function isCatalogPage() {
  return window.location.href.includes('/idees-repas.html');
}

function isPlannerPage() {
  return window.location.href.includes('/budget-repas.html');
}

function isBasketPage() {
  return window.location.href.includes('/panier');
}

function isRecapPage() {
  return window.location.href.includes('/mes-repas');
}

/**
 * Per-page SSR fetch + script injection.
 */
async function fetchFeaturesHTML(userId) {
  const headers = await buildCoursesUSSRHeaders(userId);

  if (isCatalogPage()) {
    await injectCatalog(headers);
  }
  else if (isPlannerPage()) {
    await injectPlanner(headers);
  }
  else if (isBasketPage()) {
    await prefetchRecipeTags(headers);
  }
  else if (isShelf()) {
    await prefetchRecipeCards(headers);
  }
  else if (isRecapPage()) {
    await prefetchBasketPreview(headers);
  }
  else {
    await injectCore(headers);
  }

  if (isOrderButtonsPage() && (getPosExtId()?.length === 0 || await isAvailable())) {
    await prefetchOrderButtons(headers);
  }

  await injectRecipeCardsById(headers);
}

/** ************************************ VARIABLES DETECTION ***********************************/

/**
 * We need to access window.tc_vars.user_id to init Mealz and it is not set on page startup
 * So we need to wait for it to be defined
 */
function keyVariablesAreDefined(callback) {
  waitForVariable(window, 'tc_vars', 100, 25, (tcVarsDefined) => {
    if (tcVarsDefined) {
      waitForVariable(window, 'loggedIn', 100, 25, (loggedIn) => {
        if (loggedIn !== undefined) {
          waitForVariable(window.tc_vars, 'user_id', 100, 25, callback);
        }
      });
    }
  });
}

/**
 * Wait until globalThis.mealz has been populated by the SSR-loaded module
 * scripts before invoking the lib-dependent code.
 */
function checkForMealz(callback) {
  waitForVariable(globalThis, 'mealz', 100, 50, (mealz) => {
    if (mealz === undefined) {
      console.error('[Mealz Injector] globalThis.mealz never became available');
      return;
    }
    callback();
  });
}


/*********************************** MAIN SCRIPT ***********************************/

// We wait for the userId to be defined to be sure CoursesU have filled their variables
keyVariablesAreDefined(async (user_id) => {
  const userId = globalThis.loggedIn === true ? user_id : undefined;

  // Early payment handling — runs before any await to minimise time-to-SSR-call.
  if (isOnConfirmationPage() && hasPayment()) {
    buildCoursesUSSRHeaders(userId)
      .then(headers => callSsrHandlePayment(headers, getSsrBaseUrl()));
  }

  await clearPreferences(user_id);

  await fetchSsrStyles("/v3/styles/base", getSsrBaseUrl());

  await fetchFeaturesHTML(userId);

  checkForMealz(async () => {
    try {
      await initMealz();
    } catch (error) {
      console.error('[Mealz Injector] Error in main script initialization:', error);
    }

    initRecipeRecapButton();
    if (isRecapPage()) {
      initRecapPage();
    }

    if (getPosExtId()?.length === 0 || await isAvailable()) {
      await initShelvesCardInjection();
      initBasketTagInjection();
      if (isOrderButtonsPage()) {
        injectOrderButtons();
      }
      if (isCatalogPage()) {
        listenHeaderHeightChange();
      }
    }
  });
});
